import '.d{display:flex;}.ssr-plugin'
import '.e8{line-height:1.75rem;}.ssr-plugin'
import { styled, type Style } from 'inlines'
import { ReactNode } from 'react'
import Row from '@app/components/layout/Row'
import { Icon } from '../icon/Icon'
import {
  faCircleInfo,
  faTriangleExclamation,
  faCircleCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Text } from '@app/components/core/typography/Text'

export type AlertProps = {
  variant?: Variant
  size?: Size
  children?: ReactNode
  icon?: IconProp
  style?: Style
  message?: string
  withIcon?: boolean
}

type Variant = 'error' | 'warning' | 'success' | 'info'
type Size = 'small' | 'medium'

const sizeStyle: Record<Size, Style> = {
  medium: {
    padding: '1rem',
    borderRadius: '0.9375rem',
  },
  small: {
    padding: '0.5rem 1rem',
    borderRadius: '0.625rem',
  },
}

const variantStyle: Record<Variant, Style> = {
  error: {
    color: 'var(--primary-red)',
    background: 'var(--shades-red-100)',
  },
  warning: {
    border: '1px solid var(--shades-yellow-300)',
    background: 'var(--shades-yellow-100)',
  },
  success: {
    border: '1px solid var(--success-green)',
    background: 'var(--success-mint)',
  },
  info: {
    color: 'var(--shades-blue-600)',
    border: '1px solid #D9EEFF',
    background: '#F5FBFF',
  },
}

const iconVariantMap: Record<Variant, { icon: IconProp; style: Style }> = {
  error: {
    icon: faTriangleExclamation,
    style: {
      color: 'var(--primary-red)',
    },
  },
  warning: {
    icon: faTriangleExclamation,
    style: {
      color: 'var(--sunlit-yellow)',
    },
  },
  success: {
    icon: faCircleCheck,
    style: {
      color: 'var(--success-green)',
    },
  },
  info: {
    icon: faCircleInfo,
    style: {
      color: 'var(--primary-blue)',
      background: '#F5FBFF',
    },
  },
} as const

export const Alert = ({
  children,
  variant = 'info',
  size = 'medium',
  icon,
  style,
  message,
  withIcon = true,
}: AlertProps) => {
  return (
    <styled.div className="d e8"
      style={{
        ...sizeStyle[size],
        ...variantStyle[variant],
        ...style,
      }}
    >
      <Row alignItems="center">
        {withIcon && (
          <Icon
            icon={icon ?? iconVariantMap[variant].icon}
            style={iconVariantMap[variant].style}
          />
        )}

        {message !== undefined && (
          <Text.Medium color={variantStyle[variant].color ?? 'inherit'}>
            {message}
          </Text.Medium>
        )}

        {children}
      </Row>
    </styled.div>
  )
}
